import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { LoadingGkService } from '@services/ui/loading.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { CurrentUserService } from './current-user.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly TOKEN_KEY = 'JWT_TOKEN';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';
	private readonly DEVICE_TOKEN_KEY = 'device_token';
	private readonly loginUrl = `${environment.pathBackend}/auth/login`;
	private readonly devLoginUrl = `${environment.pathBackend}/auth/dev-login`;
	private readonly registerUrl = `${environment.pathBackend}/auth/register`;
	private readonly refreshUrl = `${environment.pathBackend}/auth/refresh`;
	private refreshTokenTimeout: ReturnType<typeof setTimeout> | undefined;

	constructor(
		private http: HttpClient,
		private router: Router,
		private messageAlertService: MessageAlertService,
		private currentUserService: CurrentUserService,
		private loadingGkService: LoadingGkService,
		private ngZone: NgZone,
		private translocoService: TranslocoService,
	) {}

	/**
	 * Gets the current access token
	 * @returns The JWT token or null if it doesn't exist
	 */
	getToken(): string | null {
		return localStorage.getItem(this.TOKEN_KEY);
	}

	/**
	 * Clears all session data (tokens, user data)
	 * Used both on logout and at the start of login to ensure clean state
	 */
	clearSession(): void {
		// Stop refresh timer
		this.stopRefreshTokenTimer();

		// Clear tokens
		localStorage.removeItem(this.TOKEN_KEY);
		localStorage.removeItem(this.REFRESH_TOKEN_KEY);

		// Clear user data
		this.currentUserService.clearCurrentUser();
	}

	/**
	 * Handles navigation after login
	 */
	private handlePostLoginNavigation(): Observable<any> {
		this.translocoService.setActiveLang('es');
		this.navigateTo('/dashboard');
		return of(null);
	}

	/**
	 * Navigates to a route inside ngZone.
	 */
	private navigateTo(route: string): void {
		this.ngZone.run(() => {
			setTimeout(() => {
				this.router.navigate([route]);
			}, 150);
		});
	}

	/**
	 * Logs in by sending credentials to the backend.
	 * @param email User's email
	 * @param password User's password
	 * @returns Observable with backend response
	 */
	login(email: string, password: string): Observable<any> {
		// Clear any previous session data before starting new login
		this.clearSession();

		const deviceToken = localStorage.getItem(this.DEVICE_TOKEN_KEY);
		const loginData: any = {
			email: email,
			password: password,
		};

		// Include device token if available
		if (deviceToken) {
			loginData.deviceToken = deviceToken;
		}

		return this.http.post<any>(this.loginUrl, loginData).pipe(
			switchMap((response: any) => {
				// If MFA is required, return response without further processing
				if (response?.mfa_required) {
					return of(response);
				}

				if (!response?.access_token) {
					throw new Error('No se recibió el token de acceso');
				}

				// Save tokens
				const token = response.access_token;
				const refreshToken = response.refresh_token;
				localStorage.setItem(this.TOKEN_KEY, token);
				localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

				// Start refresh timer
				this.startRefreshTokenTimer(response);

				const employee = response.employee;
				const user = response.user;

				if (!employee) {
					throw new Error('No se recibieron los datos del usuario');
				}

				const role = employee.role;
				if (!role) {
					throw new Error('No se recibieron los datos del rol');
				}

				// Build complete user object
				const completeUserData = {
					id: user.id,
					userId: employee.userId,
					email: employee.email,
					displayName: `${employee.name} ${employee.lastName}`.trim() || 'Usuario',
					type: 'employee',
					role: {
						id: role.id,
						name: role.name,
						description: role.description,
					},
					permissions: employee.permissions || [],
					enabled: true,
					emailVerified: user.user_metadata?.email_verified || false,
					profilePhotoUrl: employee.profilePhotoUrl || null,
					preferredLanguage: 'es',
				};

				// Save user data
				this.currentUserService.setCurrentUser(completeUserData);

				// Navigate (check if user has only 1 client) and wait for it
				return this.handlePostLoginNavigation().pipe(map(() => response));
			}),
			catchError((error: HttpErrorResponse) => {
				const backendMessage = error.error?.message;

				// Translation map from backend messages to translation keys
				const translationKeys: Record<string, string> = {
					'Incorrect password': 'auth.errors.incorrectPassword',
					'User not found': 'auth.errors.userNotFound',
					'Invalid credentials': 'auth.errors.invalidCredentials',
				};

				let errorMessage: string;
				if (backendMessage && translationKeys[backendMessage]) {
					errorMessage = this.translocoService.translate(translationKeys[backendMessage]);
				} else {
					errorMessage = backendMessage || this.translocoService.translate('auth.errors.internalServerError');
				}

				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Localhost/tailnet-only development login: password-less session for a
	 * registered user. The backend hard-rejects it outside private hosts.
	 */
	devLogin(email: string): Observable<any> {
		this.clearSession();

		return this.http.post<any>(this.devLoginUrl, { email }).pipe(
			switchMap((response: any) => {
				if (!response?.access_token) {
					throw new Error('No se recibió el token de acceso');
				}

				localStorage.setItem(this.TOKEN_KEY, response.access_token);
				localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
				this.startRefreshTokenTimer(response);

				const employee = response.employee;
				const user = response.user;

				if (!employee) {
					throw new Error('No se recibieron los datos del usuario');
				}

				const role = employee.role;
				if (!role) {
					throw new Error('No se recibieron los datos del rol');
				}

				this.currentUserService.setCurrentUser({
					id: user.id,
					userId: employee.userId,
					email: employee.email,
					displayName: `${employee.name} ${employee.lastName}`.trim() || 'Usuario',
					type: 'employee',
					role: {
						id: role.id,
						name: role.name,
						description: role.description,
					},
					permissions: employee.permissions || [],
					enabled: true,
					emailVerified: user.user_metadata?.email_verified || false,
					profilePhotoUrl: employee.profilePhotoUrl || null,
					preferredLanguage: 'es',
				});

				return this.handlePostLoginNavigation().pipe(map(() => response));
			}),
			catchError((error: HttpErrorResponse) => {
				const backendMessage = error.error?.message;
				const code = error.error?.code;
				const errorMessage =
					code === 'AUTH_DEV_LOGIN_DISABLED'
						? this.translocoService.translate('auth.errors.devLoginDisabled')
						: backendMessage || this.translocoService.translate('auth.errors.internalServerError');
				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Registers a new user account
	 * @param data Registration data with email and password
	 * @returns Observable with backend response
	 */
	register(data: { email: string; password: string }): Observable<any> {
		this.loadingGkService.showLoading();

		return this.http.post<any>(this.registerUrl, data).pipe(
			tap((response) => {
				if (response.requiresEmailVerification) {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('auth.errors.checkEmailVerification'),
					);
				} else {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('auth.errors.accountCreated'),
					);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorCreatingAccount');
				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
			finalize(() => this.loadingGkService.hideLoading()),
		);
	}

	/**
	 * Verifies email using token from verification link
	 * @param token Verification token
	 * @param type Token type (optional)
	 * @returns Observable with verification response
	 */
	verifyEmail(token: string, type?: string): Observable<any> {
		this.loadingGkService.showLoading();

		return this.http.post<any>(`${environment.pathBackend}/auth/verify-email`, { token, type }).pipe(
			tap(() => {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('auth.errors.emailVerified'),
				);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorVerifyingEmail');
				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
			finalize(() => this.loadingGkService.hideLoading()),
		);
	}

	/**
	 * Resends verification email
	 * @param email Email to resend verification to
	 * @returns Observable with response
	 */
	resendVerificationEmail(email: string): Observable<any> {
		this.loadingGkService.showLoading();

		return this.http.post<any>(`${environment.pathBackend}/auth/resend-verification`, { email }).pipe(
			tap(() => {
				this.messageAlertService.showMessage(
					'info',
					this.translocoService.translate('auth.resetPassword.messages.emailSent'),
					this.translocoService.translate('auth.errors.verificationEmailSent'),
				);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorResendingEmail');
				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
			finalize(() => this.loadingGkService.hideLoading()),
		);
	}

	/**
	 * Logs out by sending refresh token to backend and removing tokens from local storage.
	 * @returns Observable with backend response
	 */
	logout(): Observable<any> {
		// Get both tokens before clearing them
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
		const accessToken = localStorage.getItem(this.TOKEN_KEY);

		// Clear all session data
		this.clearSession();

		// If both tokens exist, notify backend (both are required by external auth service)
		if (refreshToken && accessToken) {
			return this.http
				.post(`${environment.pathBackend}/auth/logout`, {
					refresh_token: refreshToken,
					access_token: accessToken,
				})
				.pipe(
					tap(() => {
						this.router.navigate(['/login']);
					}),
					catchError(() => {
						// Even if it fails, navigate to login
						this.router.navigate(['/login']);
						return of(null);
					}),
				);
		}

		// If no tokens available, just navigate to login
		this.router.navigate(['/login']);
		return of(null);
	}

	/**
	 * Checks if the user is authenticated.
	 * @returns `true` if token exists and has not expired.
	 */
	isAuthenticated(): boolean {
		const token = localStorage.getItem(this.TOKEN_KEY);
		if (!token) {
			return false;
		}

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const isExpired = payload.exp * 1000 < Date.now();

			if (isExpired) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Refreshes the access token using the refresh token
	 * @returns Observable with the new token
	 */
	private retryCount = 0;
	private maxRetries = 3;

	refreshToken(): Observable<any> {
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

		if (!refreshToken) {
			return throwError(() => new Error('No hay un token de renovación disponible'));
		}

		return this.http.post<any>(this.refreshUrl, { refresh_token: refreshToken }).pipe(
			tap((response) => {
				if (!response?.access_token || !response?.refresh_token) {
					throw new Error('La respuesta de renovación no es válida');
				}

				this.retryCount = 0; // Reset retry count on success
				localStorage.setItem(this.TOKEN_KEY, response.access_token);
				localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
				this.startRefreshTokenTimer(response);
			}),
			catchError((error: HttpErrorResponse) => {
				// 4xx means the refresh token is rejected (expired/revoked/malformed).
				// Retrying with the same token cannot succeed — log out immediately.
				const isClientError = error.status >= 400 && error.status < 500;
				const shouldRetry = !isClientError && this.retryCount < this.maxRetries;

				if (shouldRetry) {
					this.retryCount++;
					const backoffTime = 2 ** this.retryCount * 1000;
					return new Observable((subscriber) => {
						setTimeout(() => {
							this.refreshToken().subscribe(subscriber);
						}, backoffTime);
					});
				}

				this.retryCount = 0;
				// logout() returns a cold Observable — subscribe so the HTTP fires
				// and the navigation to /login actually happens.
				this.logout().subscribe();
				return throwError(() => error);
			}),
		);
	}

	/**
	 * Starts the timer to refresh the token
	 * @param tokens Access and refresh tokens
	 */
	private startRefreshTokenTimer(tokens: { access_token: string }) {
		// Decode token to get expiration time
		const jwtToken = JSON.parse(atob(tokens.access_token.split('.')[1]));
		const expiresAt = new Date(jwtToken.exp * 1000);
		const now = new Date();
		const timeUntilExpiry = expiresAt.getTime() - now.getTime();

		// If token expires in less than 2 minutes, refresh immediately
		if (timeUntilExpiry < 120000) {
			this.refreshToken().subscribe();
			return;
		}

		// Refresh when 1/3 of token lifetime remains
		const refreshDelay = Math.floor(timeUntilExpiry * 0.66);

		// Clear any existing timeout
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}

		// Set new timeout
		this.refreshTokenTimeout = setTimeout(() => {
			this.refreshToken().subscribe({
				next: () => {},
				error: () => {
					// If it fails, try again in 30 seconds
					setTimeout(() => this.refreshToken().subscribe(), 30000);
				},
			});
		}, refreshDelay);
	}

	/**
	 * Stops the token refresh timer
	 */
	private stopRefreshTokenTimer() {
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}
	}

	/**
	 * Sends a password reset email to the user
	 * @param email User's email address
	 * @returns Observable with the reset response
	 */
	resetPassword(email: string): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/reset-password`, { email }).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorSendingResetEmail');
				this.messageAlertService.showMessage('error', null, errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Exchanges the token_hash of the reset-password email link for the
	 * recovery access token that updatePasswordWithToken consumes.
	 */
	verifyRecoveryToken(tokenHash: string): Observable<{ accessToken: string }> {
		return this.http.post<{ accessToken: string }>(`${environment.pathBackend}/auth/reset-password/verify`, {
			tokenHash,
		});
	}

	/**
	 * Updates user password using the access token from reset email
	 * @param accessToken Token from reset email
	 * @param newPassword New password
	 * @returns Observable with the update response
	 */
	updatePasswordWithToken(accessToken: string, newPassword: string): Observable<any> {
		return this.http
			.post(`${environment.pathBackend}/auth/update-password`, {
				accessToken,
				newPassword,
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.errorUpdatingPassword');
					this.messageAlertService.showMessage('error', null, errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Enrolls the current user in MFA/2FA
	 * @returns Observable with enrollment data including QR code
	 */
	enrollMFA(): Observable<any> {
		const token = this.getToken();

		return this.http
			.post(
				`${environment.pathBackend}/auth/mfa/enroll`,
				{},
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.errorEnrollingMFA');
					this.messageAlertService.showMessage('error', null, errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Verifies MFA enrollment with TOTP code
	 * @param factorId Factor ID from enrollment
	 * @param code TOTP code from authenticator app
	 * @returns Observable with verification response
	 */
	verifyMFAEnrollment(factorId: string, code: string): Observable<any> {
		const token = this.getToken();

		return this.http
			.post(
				`${environment.pathBackend}/auth/mfa/verify`,
				{ factorId, code },
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.invalidVerificationCode');
					this.messageAlertService.showMessage('error', null, errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Unenrolls the current user from MFA/2FA
	 * @param factorId Factor ID to remove
	 * @returns Observable with unenrollment response
	 */
	unenrollMFA(factorId: string): Observable<any> {
		const token = this.getToken();

		return this.http
			.post(
				`${environment.pathBackend}/auth/mfa/unenroll`,
				{ factorId },
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.errorDisablingMFA');
					this.messageAlertService.showMessage('error', null, errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Gets list of MFA factors for the current user
	 * @returns Observable with list of MFA factors
	 */
	listMFAFactors(): Observable<any> {
		const token = this.getToken();

		return this.http
			.get(`${environment.pathBackend}/auth/mfa/factors`, {
				headers: { Authorization: `Bearer ${token}` },
			})
			.pipe(
				catchError(() => {
					return of({ factors: [] });
				}),
			);
	}

	/**
	 * Verifies MFA code during login
	 * @param accessToken Temporary access token from login
	 * @param factorId MFA factor ID
	 * @param code TOTP code from authenticator app
	 * @param trustDevice Whether to trust this device for 30 days
	 * @returns Observable with complete login response
	 */
	verifyMFALogin(accessToken: string, factorId: string, code: string, trustDevice: boolean = false): Observable<any> {
		// Clear any previous session data before completing MFA login
		this.clearSession();

		return this.http
			.post(`${environment.pathBackend}/auth/mfa/verify-login`, {
				accessToken,
				factorId,
				code,
				trustDevice,
			})
			.pipe(
				tap((response: any) => {
					if (!response?.access_token) {
						throw new Error('No se recibió el token de acceso después de verificar el segundo factor');
					}

					// Save device token if provided
					if (response.device_token) {
						localStorage.setItem(this.DEVICE_TOKEN_KEY, response.device_token);
					}

					// Save tokens
					const token = response.access_token;
					const refreshToken = response.refresh_token;
					localStorage.setItem(this.TOKEN_KEY, token);
					localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

					// Start refresh timer
					this.startRefreshTokenTimer(response);

					// Get employee data from response
					const employee = response.employee;
					const user = response.user;

					if (!employee) {
						throw new Error('No se recibieron los datos del usuario');
					}

					const role = employee.role;
					if (!role) {
						throw new Error('No se recibieron los datos del rol');
					}

					const completeUserData = {
						id: user.id,
						userId: employee.userId,
						email: employee.email,
						displayName: `${employee.name} ${employee.lastName}`.trim() || 'Usuario',
						type: 'employee',
						role: {
							id: role.id,
							name: role.name,
							description: role.description,
						},
						permissions: employee.permissions || [],
						enabled: true,
						emailVerified: user.user_metadata?.email_verified || false,
						profilePhotoUrl: employee.profilePhotoUrl || null,
						preferredLanguage: 'es',
					};

					// Save complete data in CurrentUserService
					this.currentUserService.setCurrentUser(completeUserData);
				}),
				// After saving user data, handle navigation (which also loads preferred language)
				switchMap((response) => this.handlePostLoginNavigation().pipe(map(() => response))),
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.invalidMFACode');
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Gets list of trusted devices for the current user
	 * @returns Observable with list of trusted devices
	 */
	getTrustedDevices(): Observable<any> {
		const token = this.getToken();

		return this.http
			.get(`${environment.pathBackend}/auth/trusted-devices`, {
				headers: { Authorization: `Bearer ${token}` },
			})
			.pipe(
				catchError(() => {
					return of({ devices: [] });
				}),
			);
	}

	/**
	 * Revokes a trusted device
	 * @param deviceId ID of the device to revoke
	 * @returns Observable with the result
	 */
	revokeTrustedDevice(deviceId: number): Observable<any> {
		const token = this.getToken();

		return this.http
			.delete(`${environment.pathBackend}/auth/trusted-devices/${deviceId}`, {
				headers: { Authorization: `Bearer ${token}` },
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					return throwError(() => error);
				}),
			);
	}
}
